import { type HTMLAttributes, type ReactNode, useState } from 'react';

import Link from 'components/Link';
import PlainButton from 'components/PlainButton';
import { PartialPopover } from 'components/Popover';
import { cn } from 'utils/classNames';

import type { ValidPopoverLink, ValidSitecoreLink } from './helpers';

interface Props extends HTMLAttributes<HTMLElement> {
	children?: ReactNode;
	className?: string;
	isScreenReaderHidden?: boolean;
	link: ValidSitecoreLink;
	underline?: boolean;
}

function WithPopover({
	children,
	className,
	isScreenReaderHidden,
	link,
	underline,
	...attrs
}: Props & { link: ValidPopoverLink }) {
	const [isOpen, setIsOpen] = useState(false);

	return (
		<>
			<PlainButton
				{...attrs}
				className={cn(
					className,
					underline && 'underline hover:underline-strong',
				)}
				onClick={(e) => {
					setIsOpen(true);
					attrs.onClick?.(e);
				}}
				aria-haspopup="dialog"
				tabIndex={isScreenReaderHidden ? -1 : attrs.tabIndex}
			>
				{children}
			</PlainButton>
			<PartialPopover
				isOpen={isOpen}
				partial={link.href}
				heading={link.title}
				onClose={() => setIsOpen(false)}
			/>
		</>
	);
}
WithPopover.displayName = 'ScLink_WithPopover';

export default function ScLink({
	children,
	isScreenReaderHidden,
	link,
	underline,
	...attrs
}: Props) {
	const content = children ?? link.text;
	if (link.linktype === 'popover') {
		return (
			<WithPopover {...attrs} link={link} underline={underline}>
				{content}
			</WithPopover>
		);
	}
	return (
		<Link
			{...attrs}
			href={link.href}
			target={link.target}
			underline={underline}
			isScreenReaderHidden={isScreenReaderHidden}
		>
			{content}
		</Link>
	);
}

ScLink.displayName = 'ScLink';
